import React, { useEffect, useState } from 'react';
import { FaSpinner } from 'react-icons/fa';
import { RxCross2 } from 'react-icons/rx';
import { toast } from 'react-toastify';
import UserApi from '../API/UserApi';
import { useAuth } from '../Auth/AuthProvider';
import Button from '../Components/Button';
import InputField from '../Components/InputField';
import LoadingIcon from '../Components/LoadingIcon';
import SelectField from '../Components/SelectField';
import { SUPPORTED_GRADES } from '../Constants/grades';
import { SUPPORTED_LEXILES } from '../Constants/lexiles';
import { AnalyticsEvent, useAnalytics } from '../Context/AnalyticsProvider';
import Modal from '../Modal/Modal';
import { useNavigate } from '../ProtectedRoute';
import { User, UserType } from '../Types/User';
import ProfilePhoto from './ProfilePhoto';

/**
 * EditProfile view
 *
 * This component displays a modal for editing the user's profile information.
 * It includes fields for first name, last name, email, grade, and lexile level.
 * The user can edit these fields and save or cancel the changes.
 */
function EditProfile({ onClose }: { onClose: (updated?: boolean) => void }) {
  const { userId, signOut } = useAuth();
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();
  const [user, setUser] = useState<User>();
  const [firstName, setFirstName] = useState<string>();
  const [lastName, setLastName] = useState<string>();
  const [email, setEmail] = useState<string>();
  const [grade, setGrade] = useState<number>();
  const [lexileLevel, setLexileLevel] = useState<string>();
  const [selectedImage, setSelectedImage] = useState<File | null>(null);

  const [isSaving, setIsSaving] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteInput, setDeleteInput] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (!userId) return;

    const userApi = new UserApi();
    userApi.getUser(userId).then((user) => {
      setUser(user);
      setFirstName(user.name);
      setLastName(user.surname);
      setEmail(user.email);
      setGrade(user.grade);
      setLexileLevel(user.lexileId);
    });
  }, [userId]);

  const handleSave = async () => {
    try {
      const userApi = new UserApi();
      const updatedUser: User = {
        ...user,
        name: firstName,
        surname: lastName,
        grade: grade,
        lexileId: lexileLevel ?? user.lexileId,
      };

      setIsSaving(true);
      let changed = false;

      if (JSON.stringify(updatedUser) !== JSON.stringify(user)) {
        const trackingMetadata = {
          userId: userId,
          firstName: firstName,
          lastName: lastName,
          grade: grade,
          lexileLevel: lexileLevel,
        };

        try {
          trackEvent(
            AnalyticsEvent.USER_PROFILE_EDIT_INFO_REQUESTED,
            trackingMetadata,
          );

          changed = true;
          await userApi.updateUser(updatedUser);

          trackEvent(
            AnalyticsEvent.USER_PROFILE_EDIT_INFO_COMPLETED,
            trackingMetadata,
          );
        } catch (error) {
          trackEvent(AnalyticsEvent.USER_PROFILE_EDIT_INFO_FAILED, {
            userId: userId,
            error: error.message,
          });
          throw error;
        }
      }

      if (selectedImage) {
        const trackingMetadata = {
          userId: userId,
        };

        try {
          trackEvent(
            AnalyticsEvent.USER_PROFILE_EDIT_IMAGE_REQUESTED,
            trackingMetadata,
          );

          changed = true;
          await userApi.updateProfileImage(userId, selectedImage);

          trackEvent(
            AnalyticsEvent.USER_PROFILE_EDIT_IMAGE_COMPLETED,
            trackingMetadata,
          );
        } catch (error) {
          trackEvent(AnalyticsEvent.USER_PROFILE_EDIT_IMAGE_FAILED, {
            userId: userId,
            error: error.message,
          });
          throw error;
        }
      }

      if (changed) {
        toast.success('Changes saved successfully!');
        onClose(true);
        await new Promise((resolve) => setTimeout(resolve, 500)); // Slight delay for a better UX
        window.location.reload();
      } else {
        onClose(true);
      }
    } catch (error) {
      toast.error(
        `Error updating profile: ${error.response?.data?.error ?? 'An unknown error occurred'}`,
      );
      console.error('Error updating profile:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const handleDeleteAccount = async () => {
    try {
      setIsDeleting(true); // Set loading state to true
      const userApi = new UserApi();
      await userApi.deleteUser(userId);
      await signOut();
    } catch (error) {
      toast.error(
        `Error deleting account: ${error.response?.data?.error ?? 'An unknown error occurred'}`,
      );
      console.error('Error deleting account:', error);
    } finally {
      setIsDeleting(false); // Set loading state to false
    }
  };

  if (!user) {
    return (
      <div
        className="flex-1 flex justify-center items-center"
        style={{ height: 'calc(100dvh - 60px)' }}
      >
        <LoadingIcon />
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-white rounded-lg max-w-[432px]">
      <header className="flex items-center justify-between px-4 py-4 bg-blue-600 rounded-t-lg">
        <h1 className="text-xl font-bold text-white">Edit Profile</h1>
        <Button onClick={handleCancel}>
          <RxCross2 className="w-4 h-4 text-white" />
        </Button>
      </header>
      <main className="flex flex-col px-4 py-6">
        <ProfilePhoto
          user={user}
          handleChange={setSelectedImage}
          showChangeText={true}
        />
        <div className="flex gap-2 mt-6 w-full pr-2">
          <InputField
            label="First Name"
            value={firstName}
            onChange={setFirstName}
            className="w-1/2"
          />
          <InputField
            label="Last Name"
            value={lastName}
            onChange={setLastName}
            className="w-1/2"
          />
        </div>
        {user.email ? (
          <InputField
            label="Email"
            value={email}
            onChange={setEmail}
            className="mt-6 disabled:opacity-50"
            disabled={true}
          />
        ) : null}
        <div
          className="mt-1 text-sm text-red-600 cursor-pointer self-start"
          onClick={() => setIsDeleteModalOpen(true)}
        >
          Delete My Account
        </div>
        {user.type === UserType.Student ? (
          <div className="flex gap-2 mt-6">
            <SelectField
              label="Maturity Level"
              value={grade?.toString()}
              onChange={(value) => setGrade(Number.parseInt(value))}
              options={SUPPORTED_GRADES.map((grade) => ({
                title: grade.title,
                value: grade.value.toString(),
              }))}
              disabled={!!user.parentId}
            />
            <SelectField
              label="Lexile Level"
              value={lexileLevel}
              onChange={setLexileLevel}
              options={SUPPORTED_LEXILES}
              disabled={!!user.parentId}
            />
          </div>
        ) : null}
      </main>
      <hr className="border-gray-200" />
      <footer className="flex justify-end px-4 py-4 bg-white rounded-lg">
        <div className="flex gap-2">
          <Button variant="secondary" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="w-26 whitespace-normal"
          >
            {isSaving ? (
              <FaSpinner
                className="animate-spin w-24"
                style={{ margin: '0 0.05rem' }}
              />
            ) : (
              'Save Changes'
            )}
          </Button>
        </div>
      </footer>
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
      >
        <div className="p-4 text-left">
          <h2 className="text-xl font-bold mb-4">Delete Account</h2>
          <InputField
            label="Are you sure you want to delete your account? This action is irreversible. Type 'delete account' to confirm."
            value={deleteInput}
            onChange={setDeleteInput}
            placeholder="delete account"
            className="mb-4 gap-0.5"
          />
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => setIsDeleteModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="error"
              onClick={handleDeleteAccount}
              disabled={deleteInput.toLowerCase() !== 'delete account'}
              className={
                deleteInput.toLowerCase() !== 'delete account' || isDeleting
                  ? 'opacity-50 cursor-not-allowed'
                  : ''
              }
            >
              {isDeleting ? (
                <FaSpinner
                  className="animate-spin w-24"
                  style={{ margin: '0 0.05rem' }}
                />
              ) : (
                'Delete'
              )}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default EditProfile;
